import { useEffect, useRef, useState } from 'react';
import dynamicfunction from '../../utils/dynamicfunction';
import { useNavigate, useSearchParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { getAccounOpeningDropdowns, getAccountOpeningFieldCodes, getAccountOpeningForm, getAccountOpeningRejectionSummary, saveAccountOpeningData, saveAccountOperation, submitAccountopeningData } from '../../store/actions/accountOpeningAction';
import { getContentKeyAccToLang, setToken } from '../../utils/utils';
import { setAccountOpening, setApplicationId } from '../../store/slices/contentSlice';
import { logoutAction } from '../../store/actions/authActions';
import { setFormStepSaved } from '../../store/slices/commonSlice';

export default () => {
  const { TextWeight, isArabic } = dynamicfunction();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { corporate_kyc_data, corporate_save_loading, rejection_summary } = useSelector((state) => state.corporate);
  const corpAOContent = useSelector((state) => state.content?.corpAOContent);
  const { common_loading, saved_form_steps } = useSelector((state) => state.common);
  const language = useSelector((state) => state.content?.language);
  const selectedAccountOpeningTab = useSelector((state) => state.content?.selectedAccountOpeningTab);
  const { applicationId } = useSelector((state) => state.content)

  const [step, setStepState] = useState(() => {

    const queryStep = Number(searchParams.get("step"));
    return queryStep || 1;
  });
  const [rejectionModal, setRejectionModal] = useState(false);
  const [isExit, setIsExit] = useState(false);
  const [isTermsAgreed, setIsTermsAgreed] = useState(false);
  const [unsavedChangesModal, setUnsavedChangesModal] = useState(false);

  const generalInformationFormikRef = useRef(null);
  const contactInformationFormikRef = useRef(null);
  const clientClassificationFormikRef = useRef(null);
  const investmentPortfolioFormikRef = useRef(null);
  const bankAndCustodianFormikRef = useRef(null);
  const accountOperationFormikRef = useRef(null);

  const getContentKey = (key) => {
    return getContentKeyAccToLang(corpAOContent?.corporate_choose_module?.data?.attributes, key, isArabic);
  };

  const forms_percentage = corporate_kyc_data?.applicationFacade;

  const [formErrorBorders, setFormErrorBorders] = useState({
    generalInformation: false,
    contactInformation: false,
    clientClassification: false,
    investmentPortfolio: false,
    bankAndCustodian: false,
    accountOperation: false
  });

  const setStep = (value) => {
    setStepState(value);
    setSearchParams({ step: String(value) }, { replace: true });
  };

  useEffect(() => {
    dispatch(getAccountOpeningForm(isArabic ? 2 : 1))
    dispatch(getAccounOpeningDropdowns(isArabic ? 2 : 1))
    if (!searchParams.get("step")) {
      setSearchParams({ step: "1" }, { replace: true });
    }
  }, [])

  useEffect(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("step", step);
      return params;
    });
  }, [step]);

  useEffect(() => {
    const queryStep = Number(searchParams.get("step")) || 1;

    if (queryStep !== step) {
      setStepState(queryStep);
    }
  }, [searchParams]);
  
  useEffect(() => {
    getData();
  }, [language])

  const getData = () => {
    dispatch(getAccountOpeningRejectionSummary({
      applicationId: applicationId,
      languageId: isArabic ? 2 : 1
    }, (response) => {
      response?.result?.length > 0 &&
        setRejectionModal(true);
    }));
    dispatch(getAccountOpeningFieldCodes(isArabic ? 2 : 1));

  }

  const handleLogout = async () => {
    const redirect_url = `${process.env.REACT_APP_OPEN_ACCOUNT_LINK}${isArabic ? 'ar' : 'en'}`;
    dispatch(setAccountOpening(null))
    dispatch(setApplicationId(null))
    setToken("")
    // make sure cleared session is written to storage before leaving the page
    dispatch(logoutAction());
    window.location.href = redirect_url;
  };

  const clearFormErrorBorder = (formKey) => {
    if (!formKey) return;

    setFormErrorBorders((prev) => {
      if (!prev?.[formKey]) return prev;
      return {
        ...prev,
        [formKey]: false,
      };
    });
  };

  const hasValidationErrors = (errors) => {

    if (!errors) return false;
    if (Array.isArray(errors)) {
      return errors.some((item) => hasValidationErrors(item));
    }
    if (typeof errors === 'object') {
      return Object.values(errors).some((item) => hasValidationErrors(item));
    }
    return Boolean(errors);
  };

  const stepFormConfig = {
    1: { ref: generalInformationFormikRef, key: 'generalInformation' },
    2: { ref: contactInformationFormikRef, key: 'contactInformation' },
    3: { ref: accountOperationFormikRef, key: 'accountOperation' },
    4: { ref: clientClassificationFormikRef, key: 'clientClassification' },
    5: { ref: investmentPortfolioFormikRef, key: 'investmentPortfolio' },
    6: { ref: bankAndCustodianFormikRef, key: 'bankAndCustodian' },
  };

  // empty or zero is not a valid value for these fields, send null instead of 0
  const numberOrNull = (value) => (value === undefined || value === null || value === '' || Number(value) === 0 ? null : Number(value));

  const handleSave = async (isProceed = false) => {
    const currentFormConfig = stepFormConfig[step];
    const currentErrors = await currentFormConfig?.ref?.current?.validateForm();
    const hasErrors = hasValidationErrors(currentErrors);


    if (currentFormConfig?.key) {
      setFormErrorBorders((prev) => ({ ...prev, [currentFormConfig.key]: hasErrors }));
    }

    if (hasErrors) {
      currentFormConfig?.ref?.current?.handleSubmit?.();
      return;
    }

    const generalInformationValues = generalInformationFormikRef?.current?.values;
    const contactInformationValues = contactInformationFormikRef?.current?.values;
    const clientClassificationValues = clientClassificationFormikRef?.current?.values;
    const investmentPortfolioValues = investmentPortfolioFormikRef?.current?.values;
    const bankAndCustodianValues = bankAndCustodianFormikRef?.current?.values;
    const accountOperationValues = accountOperationFormikRef?.current?.values;

    const generalInformation = {
      corpClientApplicationGeneralinformationId: generalInformationValues?.corpClientApplicationGeneralinformationId,
      commercialRegistrationName: generalInformationValues?.commercialRegistrationName,
      commercialRegistrationNameEn: generalInformationValues?.commercialRegistrationNameEn,
      commercialRegistrationNo: generalInformationValues?.commercialRegistrationNo,
      expiryDateOfCR: generalInformationValues?.expiryDateOfCR,
      expiryDateOfCRGRG: generalInformationValues?.expiryDateOfCRGRG,
      unifiedNumber: generalInformationValues?.unifiedNumber,
      countryOfRegistration: generalInformationValues?.countryOfRegistration,
      dateOfIncorporation: generalInformationValues?.dateOfIncorporation,
      dateOfIncorporationGRG: generalInformationValues?.dateOfIncorporationGRG,
      countryOfPracticingBusiness: generalInformationValues?.countryOfPracticingBusiness,
      paidUpCapital: numberOrNull(generalInformationValues?.paidUpCapital),
      annualTurnover: numberOrNull(generalInformationValues?.annualTurnover),
      noOfEmployees: numberOrNull(generalInformationValues?.noOfEmployees),
      phoneNumber: `${generalInformationValues?.phoneCountry?.dial_code ?? ''}${generalInformationValues?.phoneNumber ?? ''}`,
      faxNumber: `${generalInformationValues?.faxCountry?.dial_code ?? ''}${generalInformationValues?.faxNumber ?? ''}`,
      legalStructure: generalInformationValues?.legalStructure,
      legalStructureOther: generalInformationValues?.legalStructureOther,
      nameOfAllNaturalPersonsWhoOwnOrControlCompanyShares: generalInformationValues?.nameOfAllNaturalPersonsWhoOwnOrControlCompanyShares,
      mainActivityList: generalInformationValues?.mainActivityList,
      managerList: generalInformationValues?.managerList?.map((manager) => ({
        ...manager,
        corpClientApplicationGeneralInformationId: generalInformationValues?.corpClientApplicationGeneralinformationId,
      })),
    }
    const contactInformation = {
      corpClientApplicationContactPersonInfoId: contactInformationValues?.corpClientApplicationContactPersonInfoId,
      contactPersonName: contactInformationValues?.contactPersonName,
      jobTitle: contactInformationValues?.jobTitle,
      workPhoneNumber: `${contactInformationValues?.workPhoneCountry?.dial_code ?? ''}${contactInformationValues?.workPhoneNumber ?? ''}`,
      mobileNumber: `${contactInformationValues?.mobileNumberCountry?.dial_code ?? ''}${contactInformationValues?.mobileNumber ?? ''}`,
      address: contactInformationValues?.address,
      workEmail: contactInformationValues?.workEmail,
      preferredLanguageForCorrespondence: contactInformationValues?.preferredLanguageForCorrespondence,
      buildingNumberKsa: contactInformationValues?.buildingNumberKsa,
      streetNameKsa: contactInformationValues?.streetNameKsa,
      districtKsa: contactInformationValues?.districtKsa,
      cityKsa: contactInformationValues?.cityKsa,
      postalCodeKsa: contactInformationValues?.postalCodeKsa,
      additionalNumberKsa: contactInformationValues?.additionalNumberKsa,
      unitNoKsa: contactInformationValues?.unitNoKsa,
      addressNonKsa: contactInformationValues?.addressNonKsa,
      cityNonKsa: contactInformationValues?.cityNonKsa,
      postalOrZipCodeNonKsa: contactInformationValues?.postalOrZipCodeNonKsa,
      provinceStateOrRegionNonKsa: contactInformationValues?.provinceStateOrRegionNonKsa,
      countryNonKsa: contactInformationValues?.countryNonKsa,
    }
    const clientClassification = {
      corpClientApplicationClientClassificationId: clientClassificationValues?.corpClientApplicationClientClassificationId,
      clientClassificationId: clientClassificationValues?.clientClassificationId,
      ifInstitutionalInvestorSelectOneOfTheFollowingCriteria: clientClassificationValues?.ifInstitutionalInvestorSelectOneOfTheFollowingCriteria,
      ifQualifiedInvestorSelectOneOfTheFollowingCriteria: clientClassificationValues?.ifQualifiedInvestorSelectOneOfTheFollowingCriteria,
      whatIsTheSourceOfFundsForThisAccount: clientClassificationValues?.whatIsTheSourceOfFundsForThisAccount,
      doesInstitutionFieldWorkRelateToInvestingInFinancialSecurities: clientClassificationValues?.doesInstitutionFieldWorkRelateToInvestingInFinancialSecurities,
      howYouEvaluateYourInvestmentKnowledgeAndExperience: clientClassificationValues?.howYouEvaluateYourInvestmentKnowledgeAndExperience,
      howOftenHaveTheInstituionInvestedInFinancialSecurities: clientClassificationValues?.howOftenHaveTheInstituionInvestedInFinancialSecurities,
      estimateTotaVolumeOfThoseTransactionsInSAR: clientClassificationValues?.estimateTotaVolumeOfThoseTransactionsInSAR,
      howHasTheInstitutionMadeTheseInvestments: clientClassificationValues?.howHasTheInstitutionMadeTheseInvestments,
      forHowManyYearsHasTheInstitutionInvestedInFinancialSecurities: clientClassificationValues?.forHowManyYearsHasTheInstitutionInvestedInFinancialSecurities,
      hasTheInstitutionConductedAnyMarginTransactionsInThePastFiveYears: clientClassificationValues?.hasTheInstitutionConductedAnyMarginTransactionsInThePastFiveYears,
      estimateTotalVolumeOfYourInstitutionMarginInThePastFiveYears: clientClassificationValues?.hasTheInstitutionConductedAnyMarginTransactionsInThePastFiveYears ? clientClassificationValues?.estimateTotalVolumeOfYourInstitutionMarginInThePastFiveYears : null,
      classificationFundCurrentLocations: clientClassificationValues?.classificationFundCurrentLocations,
      clientClassificationTypeOfInvestments: clientClassificationValues?.clientClassificationTypeOfInvestments,
      edaaClassification: clientClassificationValues?.edaaClassification ?? null,
      cJadwaClassification: clientClassificationValues?.jadwaClassification ?? null,
      netWorthClassification: clientClassificationValues?.netWorthClassification ?? null,
    }
    const investmentPortfolio = {
      corpClientApplicationInvestmentPortfolioId: investmentPortfolioValues?.corpClientApplicationInvestmentPortfolioId,
      doYouHaveAnExistingPortfolio: investmentPortfolioValues?.doYouHaveAnExistingPortfolio,
      whatIsTheValueOfTheInstitutionsExistingPortfolio: investmentPortfolioValues?.doYouHaveAnExistingPortfolio ? Number(investmentPortfolioValues?.whatIsTheValueOfTheInstitutionsExistingPortfolio) ?? 0 : null,
      howLongHasTheInstitutionHeldTheseInvestments: investmentPortfolioValues?.doYouHaveAnExistingPortfolio ? investmentPortfolioValues?.howLongHasTheInstitutionHeldTheseInvestments : null,
      existingPublicEquity: investmentPortfolioValues?.doYouHaveAnExistingPortfolio ? Number(investmentPortfolioValues?.existingPublicEquity) ?? 0 : null,
      existingPrivateEquity: investmentPortfolioValues?.doYouHaveAnExistingPortfolio ? Number(investmentPortfolioValues?.existingPrivateEquity) ?? 0 : null,
      existingRealEstate: investmentPortfolioValues?.doYouHaveAnExistingPortfolio ? Number(investmentPortfolioValues?.existingRealEstate) ?? 0 : null,
      existingPrivateCredit: investmentPortfolioValues?.doYouHaveAnExistingPortfolio ? Number(investmentPortfolioValues?.existingPrivateCredit) ?? 0 : null,
      existingFixedIncomeSukuk: investmentPortfolioValues?.doYouHaveAnExistingPortfolio ? Number(investmentPortfolioValues?.existingFixedIncomeSukuk) ?? 0 : null,
      existingMoneyMarketMurabaha: investmentPortfolioValues?.doYouHaveAnExistingPortfolio ? Number(investmentPortfolioValues?.existingMoneyMarketMurabaha) ?? 0 : null,
      existingTotal: investmentPortfolioValues?.doYouHaveAnExistingPortfolio ? 100 : null,
      hasInstitutionInvestedInFinancialSecuritiesOutsideKSA: investmentPortfolioValues?.doYouHaveAnExistingPortfolio ? investmentPortfolioValues?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA : null,
      estimateTotalSizeOfInstitutionInvestmentsInFinancialSecuritieOutsideKSA: investmentPortfolioValues?.doYouHaveAnExistingPortfolio && investmentPortfolioValues?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA ? investmentPortfolioValues?.estimateTotalSizeOfInstitutionInvestmentsInFinancialSecuritieOutsideKSA : null,
      investmentPortfolioCountries: investmentPortfolioValues?.doYouHaveAnExistingPortfolio && investmentPortfolioValues?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA ? investmentPortfolioValues?.investmentPortfolioCountries : null,
      whatIsTheInstitutionsDebtToInvestmentRatio: investmentPortfolioValues?.doYouHaveAnExistingPortfolio ? investmentPortfolioValues?.whatIsTheInstitutionsDebtToInvestmentRatio : null,
      whatIsTheInstitutionsGeneralInvestmentObjective: investmentPortfolioValues?.whatIsTheInstitutionsGeneralInvestmentObjective,
      investmentPortfolioPreferredCurrencies: investmentPortfolioValues?.investmentPortfolioPreferredCurrencies,
      whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments: investmentPortfolioValues?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments,
      whaIsTheInstitutionsRiskAppetite: investmentPortfolioValues?.whaIsTheInstitutionsRiskAppetite,
      targetPublicEquity: Number(investmentPortfolioValues?.targetPublicEquity) ?? 0,
      targetPrivateEquity: Number(investmentPortfolioValues?.targetPrivateEquity) ?? 0,
      targetRealEstate: Number(investmentPortfolioValues?.targetRealEstate) ?? 0,
      targetPrivateCredit: Number(investmentPortfolioValues?.targetPrivateCredit) ?? 0,
      targetFixedIncomeSukuk: Number(investmentPortfolioValues?.targetFixedIncomeSukuk) ?? 0,
      targetMoneyMarketMurabaha: Number(investmentPortfolioValues?.targetMoneyMarketMurabaha) ?? 0,
      targetTotal: 100,
      isThereAnyOtherFinancialInformationThatYouWouldLikeToDiscloseOnBehalfOfTheInstitution: investmentPortfolioValues?.isThereAnyOtherFinancialInformationThatYouWouldLikeToDiscloseOnBehalfOfTheInstitution,
      investmentPortfolioForeignCurrencies: investmentPortfolioValues?.investmentPortfolioPreferredCurrencies ? investmentPortfolioValues?.investmentPortfolioForeignCurrencies ?? [] : [],
      relationshipManagerName: investmentPortfolioValues?.relationshipManagerName,
      investmentPortfolioAccountActivities: investmentPortfolioValues?.investmentPortfolioAccountActivities ?? [],
    }
    const bankAndCustodianRequest = {
      bankTypeId: bankAndCustodianValues?.bankTypeId,
      internationalBank: bankAndCustodianValues?.internationalBank,
      ksaBank: bankAndCustodianValues?.ksaBank,
      custodianInformationSaveRequest: {
        corpClientApplicationCustodianInformationId: bankAndCustodianValues?.corpClientApplicationCustodianInformationId,
        custodianName: bankAndCustodianValues?.isHaveCustodian ? bankAndCustodianValues?.custodianName : null,
        address: bankAndCustodianValues?.isHaveCustodian ? bankAndCustodianValues?.address : null,
        accountName: bankAndCustodianValues?.isHaveCustodian ? bankAndCustodianValues?.accountName : null,
        accountNumber: bankAndCustodianValues?.isHaveCustodian ? bankAndCustodianValues?.accountNumber : null,
        toWhomShouldWeSendEachOfTheFollowingClient: bankAndCustodianValues?.toWhomShouldWeSendEachOfTheFollowingClient,
        toWhomShouldWeSendEachOfTheFollowingClientOther: bankAndCustodianValues?.toWhomShouldWeSendEachOfTheFollowingClientOther,
        toWhomShouldWeSendEachOfTheFollowingDividends: bankAndCustodianValues?.toWhomShouldWeSendEachOfTheFollowingDividends,
        toWhomShouldWeSendEachOfTheFollowingDividendsOther: bankAndCustodianValues?.toWhomShouldWeSendEachOfTheFollowingDividendsOther,
        toWhomShouldWeSendEachOfTheFollowingSalesProceeds: bankAndCustodianValues?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds,
        toWhomShouldWeSendEachOfTheFollowingSalesProceedsOther: bankAndCustodianValues?.toWhomShouldWeSendEachOfTheFollowingSalesProceedsOther,
        isHaveCustodian: bankAndCustodianValues?.isHaveCustodian,
      }
    }
    const accountOperation = {
      accountManagement: accountOperationValues?.accountManagement,
      accountManagementOther: accountOperationValues?.accountManagementOther,
      accountOperationAuthSigns: accountOperationValues?.accountOperationAuthSigns,
      ClientApplicationId: accountOperationValues?.ClientApplicationId,
    }
    const formData = new FormData();

    formData.append(
      "accountManagement",
      accountOperation.accountManagement
    );

    formData.append(
      "accountManagementOther",
      accountOperation.accountManagementOther || ""
    );

    formData.append(
      "ClientApplicationId",
      accountOperation.ClientApplicationId
    );

    formData.append(
      "languageId",
      isArabic ? 2 : 1
    )

    accountOperation?.accountOperationAuthSigns?.forEach((signatory, index) => {
      Object.keys(signatory).forEach((key) => {
        const value = signatory[key];

        if (value !== null && value !== undefined) {
          formData.append(
            `accountOperationAuthSigns[${index}].${key}`,
            value
          );
        }
      });
    });
    const corpclientapplicationid = applicationId
    const languageId = isArabic ? 2 : 1
    const api_data =
      step === 1 ? { generalInformation, corpclientapplicationid, languageId } : step === 2 ?
        { contactInformation, corpclientapplicationid, languageId } : step === 3 ?
          { accountOperation, corpclientapplicationid, languageId } : step === 4 ?
            { clientClassification, corpclientapplicationid, languageId } : step === 5 ?
              { investmentPortfolio, corpclientapplicationid, languageId } : step === 6 ?
                { bankAndCustodianRequest, corpclientapplicationid, languageId } : step === 8 ?
                  { bankAndCustodianRequest, corpclientapplicationid, languageId } : null

    if (step === 3) {
      dispatch(saveAccountOperation(formData, () => {
        dispatch(setFormStepSaved(step));
        dispatch(getAccountOpeningForm(isArabic ? 2 : 1, () => setStep(step + 1)));
      }));
    } else if (step === 8) {
      if (!isProceed) {
        navigate("/progress-saved", { replace: true });
        setIsExit(false)
        return;
      }
      dispatch(setFormStepSaved(step));
      setStep(step + 1);
    } else if (step === 9) {
      if (isProceed) {
        const submittedApplicationId = corporate_kyc_data?.clientApplicationId;
        dispatch(submitAccountopeningData({ clientApplicantId: submittedApplicationId, languageId: isArabic ? 2 : 1 }, () => {
          // session is cleared on the submit-application page; clearing it here makes the form's Container redirect to /login
          navigate(`/submit-application?applicationId=${submittedApplicationId}`, { replace: true });
        }));
      } else {
        navigate("/progress-saved", { replace: true });
        setIsExit(false)
      }
    } else {
      dispatch(saveAccountOpeningData(api_data, () => {
        dispatch(setFormStepSaved(step));
        if (!isProceed) {
          navigate("/progress-saved", { replace: true });
          setIsExit(false)
          return;
        }
        dispatch(getAccountOpeningForm(isArabic ? 2 : 1, () => {
          step === 6 ? setStep(step + 2) : setStep(step + 1);
        }));
      }));
    }
  }

  return {
    navigate,
    TextWeight,
    getContentKey,
    forms_percentage,
    corporate_kyc_data,
    corporate_save_loading,
    clearFormErrorBorder,
    formErrorBorders,
    generalInformationFormikRef,
    contactInformationFormikRef,
    clientClassificationFormikRef,
    investmentPortfolioFormikRef,
    bankAndCustodianFormikRef,
    accountOperationFormikRef,
    handleSave,
    handleLogout,
    isArabic,
    setRejectionModal,
    rejection_summary,
    rejectionModal,
    step,
    setStep,
    selectedAccountOpeningTab,
    isExit,
    setIsExit,
    isTermsAgreed,
    setIsTermsAgreed,
    unsavedChangesModal,
    setUnsavedChangesModal,
    saved_form_steps,
    common_loading,
  };
};
